import React from "react";
import DateFnsUtils from "@date-io/date-fns";
import { MuiPickersUtilsProvider, KeyboardDatePicker } from "@material-ui/pickers";
import { useCompany } from "../../Context/CompanyContext";

// Expiry dates travel through the app as plain "YYYY-MM-DD" strings (what the
// server stores and the Excel sheets use). The picker works with Date objects,
// so convert with LOCAL parts both ways - never toISOString(), which would shift
// the day for anyone east or west of UTC.
export const ymdToDate = (ymd) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(ymd || ""));
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};
export const dateToYmd = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

const DAY_MS = 24 * 60 * 60 * 1000;
const daysFromToday = (date) => {
  const today = new Date();
  const a = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  const b = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((a - b) / DAY_MS);
};

const HINT = {
  expired: { color: "#b91c1c", background: "#fee2e2" },
  near: { color: "#b45309", background: "#fef3c7" },
  ok: { color: "#15803d", background: "#dcfce7" },
};

// Calendar popup + typing allowed, clearable, with a small colour-coded hint
// ("Expires in 12 days" / "Already expired") so a typo like the wrong year is
// obvious at a glance. Past dates are allowed on purpose - stock can arrive
// already expired - the hint just makes that visible.
const ExpiryDatePicker = ({
  value,
  onChange,
  label,
  error,
  helperText,
  disabled,
  width = 170,
  showHint = true,
  inputVariant = "outlined",
  autoFocus,
}) => {
  const { company } = useCompany();
  const nearDays = Number(company?.nearExpiryDays) || 30;
  const date = ymdToDate(value);
  const days = date ? daysFromToday(date) : null;
  const level = days === null ? null : days < 0 ? "expired" : days <= nearDays ? "near" : "ok";

  return (
    <div style={{ width, maxWidth: "100%" }}>
      <MuiPickersUtilsProvider utils={DateFnsUtils}>
        <KeyboardDatePicker
          autoOk
          clearable
          disableToolbar
          variant="inline"
          inputVariant={inputVariant}
          size="small"
          margin="none"
          fullWidth
          format="dd MMM yyyy"
          placeholder="Pick expiry date"
          label={label}
          value={date}
          disabled={disabled}
          autoFocus={autoFocus}
          error={error}
          helperText={helperText}
          invalidDateMessage="Use a valid date"
          // Only report real dates (or an emptied box). While someone is mid-way
          // through typing, an invalid date must not wipe what they've typed.
          onChange={(picked, text) => {
            if (picked === null && !text) onChange("");
            else if (picked && !Number.isNaN(picked.getTime())) onChange(dateToYmd(picked));
          }}
          KeyboardButtonProps={{ size: "small", "aria-label": "Open calendar" }}
          InputProps={{ style: { fontSize: 13.5, fontWeight: 500, background: "#fff" } }}
          PopoverProps={{ style: { zIndex: 10100 } }}
        />
      </MuiPickersUtilsProvider>
      {showHint && level && (
        <div
          style={{
            display: "inline-block",
            marginTop: 3,
            padding: "1px 8px",
            borderRadius: 9999,
            fontSize: 11,
            fontWeight: 700,
            ...HINT[level],
          }}
        >
          {level === "expired"
            ? days === -1
              ? "Expired yesterday"
              : `Expired ${Math.abs(days)} days ago`
            : days === 0
            ? "Expires today"
            : `Expires in ${days} day${days === 1 ? "" : "s"}`}
        </div>
      )}
    </div>
  );
};

export default ExpiryDatePicker;
